import React from 'react';
import { LinkButton } from '@sato/components';
import { observer } from 'mobx-react-lite';

import { ILinkButtonFields, IWidget, ParsedEntry } from '@/contentful/types';

import { Link as SatoLink } from '@components/common/Link';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useParkingSpaceContracts } from '@hooks/useParkingSpaceContracts';
import { ROUTES } from '@routes/routes.constants';
import { isEligibleToManageParkingContract } from '@views/parking/parking.utils';

import { linkButtonVariantMap } from '../../utils/entry.utils';

import { WidgetButtonLayout } from './widgetButton.utils';

/**
 * Link to terminating a parking space contract. Shown only when the user has a parking contract they are allowed
 * to terminate (same rule as the termination link on the parking front page). The termination form selects the
 * contract itself, automatically if there is only one.
 *
 * Contentful options:
 * - `customTranslations.description`: text shown above the button, hidden together with the button
 * - `customTranslations.buttonText`: button text, defaults to the app translation
 * - `parameters.variant`: `"contained" | "outlined" | "text"`, same values as in Link Button
 */
export const ParkingSpaceTerminationWidget = observer(
  ({ customTranslations, parameters }: ParsedEntry<IWidget>) => {
    const { t, tContentful } = useCustomTranslations(customTranslations);
    const variant = parameters?.variant as ILinkButtonFields['variant'];
    const { all, isLoading } = useParkingSpaceContracts();

    // No contract ID is passed, the termination form lets the user choose the contract
    const hasTerminableContract = all.some(isEligibleToManageParkingContract);

    if (isLoading || !hasTerminableContract) return null;

    return (
      <WidgetButtonLayout description={t('description')}>
        <LinkButton<SatoLink<typeof ROUTES.PARKING_TERMINATION>>
          linkComponent={SatoLink}
          to={ROUTES.PARKING_TERMINATION}
          variant={linkButtonVariantMap[variant ?? 'no variant']}
          iconPosition="right"
          icon="agreementTerminate"
          className="w-fit"
        >
          {t('buttonText') || tContentful('parking.terminate_parking_contract')}
        </LinkButton>
      </WidgetButtonLayout>
    );
  }
);
